import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { MessageCircle, Mic, Brain, Heart, CheckCircle, ArrowRight } from 'lucide-react'
import { VoiceRecorder } from '../voice/VoiceRecorder'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { analyzeVoice } from '../../lib/elevenlabs'
import { performAIAnalysis } from '../../lib/ai-analysis'
import toast from 'react-hot-toast'

interface VoiceAssessmentProps {
  employeeId: string
  onComplete: (results: any) => void
  questions?: string[]
}

const defaultQuestions = [
  "How are you feeling about your work today?",
  "What's been the highlight of your week so far?",
  "Is there anything that's been challenging or stressful?",
  "How would you describe your energy levels lately?",
  "What's one thing that would make your work experience better?"
]

export const VoiceAssessment: React.FC<VoiceAssessmentProps> = ({
  employeeId,
  onComplete,
  questions = defaultQuestions
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [responses, setResponses] = useState<any[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [assessmentComplete, setAssessmentComplete] = useState(false)
  const [results, setResults] = useState<any>(null)

  const currentQuestion = questions[currentQuestionIndex]
  const isLastQuestion = currentQuestionIndex === questions.length - 1

  const handleRecordingComplete = async (audioBlob: Blob, duration: number) => {
    setIsProcessing(true)
    
    try {
      // Analyze the voice recording
      const voiceAnalysis = await analyzeVoice(audioBlob)
      
      // Perform AI analysis
      const aiAnalysis = await performAIAnalysis({
        text: voiceAnalysis.transcript,
        employee_context: {
          id: employeeId,
          department: 'Engineering', // Would come from employee data
          role: 'Developer',
          tenure_months: 12,
          previous_assessments: responses.length
        },
        analysis_type: 'comprehensive'
      })

      // Store the response
      const response = {
        question_index: currentQuestionIndex,
        question: currentQuestion,
        audio_blob: audioBlob,
        duration,
        transcript: voiceAnalysis.transcript,
        sentiment_score: voiceAnalysis.sentiment,
        stress_indicators: voiceAnalysis.stress_indicators,
        ai_analysis: aiAnalysis,
        timestamp: new Date().toISOString()
      }

      setResponses(prev => [...prev, response])

      if (isLastQuestion) {
        // Complete the assessment
        await completeAssessment([...responses, response])
      } else {
        // Move to next question
        setCurrentQuestionIndex(prev => prev + 1)
      }
    } catch (error) {
      console.error('Error processing recording:', error)
      toast.error('Failed to process recording. Please try again.')
    } finally {
      setIsProcessing(false)
    }
  }

  const completeAssessment = async (allResponses: any[]) => {
    try {
      // Calculate overall metrics
      const overallSentiment = allResponses.reduce((sum, r) => sum + r.sentiment_score, 0) / allResponses.length
      const overallStress = allResponses.reduce((sum, r) => sum + r.stress_indicators.voice_tremor, 0) / allResponses.length
      
      // Combine all transcripts for comprehensive analysis
      const combinedText = allResponses.map(r => r.transcript).join(' ')
      
      const finalAnalysis = await performAIAnalysis({
        text: combinedText,
        employee_context: {
          id: employeeId,
          department: 'Engineering',
          role: 'Developer',
          tenure_months: 12,
          previous_assessments: 0
        },
        analysis_type: 'comprehensive'
      })

      const assessmentResults = {
        employee_id: employeeId,
        session_id: `session_${Date.now()}`,
        responses: allResponses,
        overall_metrics: {
          sentiment_score: overallSentiment,
          stress_level: overallStress,
          engagement_level: finalAnalysis.results.engagement_level,
          burnout_risk: finalAnalysis.results.burnout_risk
        },
        ai_insights: finalAnalysis,
        recommendations: finalAnalysis.results.recommendations,
        completed_at: new Date().toISOString()
      }

      setResults(assessmentResults)
      setAssessmentComplete(true)
      onComplete(assessmentResults)
      
      toast.success('Assessment completed successfully!')
    } catch (error) {
      console.error('Error completing assessment:', error)
      toast.error('Failed to complete assessment')
    }
  }

  const skipQuestion = () => {
    if (isLastQuestion) {
      completeAssessment(responses)
    } else {
      setCurrentQuestionIndex(prev => prev + 1)
    }
  }

  if (assessmentComplete && results) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto"
      >
        <Card className="p-8 text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          
          <h2 className="text-2xl font-bold text-slate-900 mb-4">
            Assessment Complete!
          </h2>
          
          <p className="text-slate-600 mb-6">
            Thank you for sharing your thoughts. Your responses help us better understand and support your wellbeing.
          </p>

          {/* Quick insights */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-sage-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-sage-700">
                {Math.round(results.overall_metrics.sentiment_score * 100)}%
              </div>
              <div className="text-sm text-sage-600">Overall Sentiment</div>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-700">
                {Math.round(results.overall_metrics.engagement_level * 100)}%
              </div>
              <div className="text-sm text-blue-600">Engagement Level</div>
            </div>
          </div>

          {/* Key recommendations */}
          {results.recommendations.length > 0 && (
            <div className="text-left mb-6">
              <h3 className="font-semibold text-slate-900 mb-3">Personalized Recommendations:</h3>
              <ul className="space-y-2">
                {results.recommendations.slice(0, 3).map((rec: string, index: number) => (
                  <li key={index} className="flex items-start">
                    <div className="w-2 h-2 bg-sage-500 rounded-full mt-2 mr-3 flex-shrink-0" />
                    <span className="text-sm text-slate-600">{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Button
            onClick={() => window.location.reload()}
            className="bg-sage-600 hover:bg-sage-700 text-white"
          >
            Take Another Assessment
          </Button>
        </Card>
      </motion.div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Progress indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-600">
            Question {currentQuestionIndex + 1} of {questions.length}
          </span>
          <span className="text-sm text-slate-500">
            {Math.round(((currentQuestionIndex + 1) / questions.length) * 100)}% Complete
          </span>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-2">
          <motion.div
            className="bg-gradient-to-r from-sage-500 to-emerald-500 h-2 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
      </div>

      {/* Current question */}
      <Card className="p-8 mb-6">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageCircle className="w-6 h-6 text-sage-600" />
          </div>
          
          <h2 className="text-xl font-semibold text-slate-900 mb-2">
            Voice Check-in
          </h2>
          
          <p className="text-lg text-slate-700 leading-relaxed">
            {currentQuestion}
          </p>
        </div>

        {/* Voice recorder */}
        {!isProcessing ? (
          <VoiceRecorder
            onRecordingComplete={handleRecordingComplete}
            maxDuration={120} // 2 minutes per question
            className="mb-6"
          />
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Brain className="w-8 h-8 text-sage-600 animate-pulse" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Processing Your Response
            </h3>
            <p className="text-slate-600">
              Our AI is analyzing your voice and generating insights...
            </p>
          </div>
        )}

        {/* Skip option */}
        <div className="text-center">
          <Button
            variant="ghost"
            onClick={skipQuestion}
            disabled={isProcessing}
            className="text-slate-500 hover:text-slate-700"
          >
            Skip this question
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </Card>

      {/* Assessment info */}
      <div className="text-center text-sm text-slate-500">
        <div className="flex items-center justify-center space-x-6">
          <div className="flex items-center">
            <Heart className="w-4 h-4 mr-2 text-sage-500" />
            <span>Confidential & Secure</span>
          </div>
          <div className="flex items-center">
            <Brain className="w-4 h-4 mr-2 text-blue-500" />
            <span>AI-Powered Insights</span>
          </div>
        </div>
      </div>
    </div>
  )
}